<template>
  <div>
    <div
      class="welcome-message-container"
      v-if="welcomeMessage || welcomeAnimationState === 'leaving'"
      :class="{
        'welcome-enter': welcomeAnimationState === 'entering',
        'welcome-leave': welcomeAnimationState === 'leaving'
      }"
    >
      <div
        class="welcome-message"
        :class="{
          'message-enter': welcomeAnimationState === 'entering',
          'message-leave': welcomeAnimationState === 'leaving'
        }"
        :style="welcomeMessageStyle"
      >
        <div class="scrolling-container">
          <div class="scrolling-text">{{ welcomeMessage }}</div>
          <div class="scrolling-text scrolling-text-duplicate">{{ welcomeMessage }}</div>
        </div>
      </div>
    </div>
    <div class="main-content-wrapper" :class="{ 'with-welcome': welcomeMessage }">
      <div class="top-info">
        <div class="date-info">
          <div class="time">{{ currentTime }}</div>
          <div class="top-wd">
            <div class="weekday">{{ currentWeekday }}</div>
            <div class="date">{{ currentDate }}</div>
          </div>
        </div>
        <div class="top-bar"><img src="@/assets/top.png" alt="头部图片" /></div>
        <div class="current-count">
          当日元气恢复人数:
          <div class="digit-container">
            <div
              v-for="(digit, index) in String(ongoingTreatmentCount || 0).padStart(3, '0')"
              :key="`digit-${index}`"
              class="digit"
            >
              {{ digit }}
            </div>
          </div>
        </div>
      </div>
      <div class="modules-wrapper" ref="modulesWrapper">
        <div
          class="frame-item"
          v-for="slot in patientSlotPool"
          :key="'slot-' + slot.slotIndex"
          v-show="slot.visible"
          :class="{'fade-in': slot.item && slot.item.isNew}"
        >
          <template v-if="slot.visible && slot.item">
            <div class="top-content" :class="getTreatmentStatusClass(slot.item)">
              <div class="room-info">
                <img :src="getTreatmentStatusIcon(slot.item)" alt="状态图标" class="status-icon" loading="lazy" />
                {{ slot.item.roomName }}
              </div>
              <div class="treatment-therapist-container">
                <div class="time-info">
                  {{ getTreatmentStatus(slot.item) }} ({{ formatTimeDisplay(slot.item.startTime) }}~{{ formatTimeDisplay(slot.item.endTime) }})
                </div>
                <span>|</span>
                <div class="therapist-info">治疗师 : {{ formatTherapistName(slot.item.doctorName) }}</div>
              </div>
            </div>
            <div class="main-content">
              <div class="character-section">
                <div class="character-map">
                  <img :src="getCharacterImage(slot.item)" alt="人物图" loading="lazy" />
                  <div class="bubble-container">
                    <div v-for="bubble in slot.item.bubbles" :key="bubble.id" class="sleep-bubble" v-show="bubble.visible" :style="{ opacity: bubble.opacity || 1, top: bubble.position && bubble.position.top, left: bubble.position && bubble.position.left, right: bubble.position && bubble.position.right, bottom: bubble.position && bubble.position.bottom }">
                      <div class="bubble-bg"></div>
                      <div class="bubble-text">+{{ bubble.value }}</div>
                    </div>
                  </div>
                </div>
                <div class="patient-huanzhe">
                  <div class="patient-name-container">
                    <img :src="slot.item && slot.item.patientGender === 1 ? require('@/assets/icon_male.png') : require('@/assets/icon_female.png')" class="gender-icon" alt="性别图标" loading="lazy" />
                    <span style="color: #00122E;font-weight: bold;">{{ formatPatientName(slot.item.patientName) }}</span>
                    <div class="sleep-status" :class="{'completed-status': getTreatmentStatus(slot.item) === '已结束', 'ongoing-status': getTreatmentStatus(slot.item) === '治疗中'}">
                      {{ getSleepStatus(slot.item) }}
                    </div>
                  </div>
                  <div class="bottom-content">
                    <div class="progress-container">
                      <div class="custom-progress-bar">
                        <div class="progress-track">
                          <div class="progress-fill" :style="{ width: Math.min(getProgressPercentage(slot.item), 100) + '%' }">
                            <div class="progress-glow"></div>
                            <div class="progress-percentage">{{ getProgressPercentage(slot.item) }}%</div>
                          </div>
                        </div>
                      </div>
                      <div class="progress-info">
                        <div class="progress-label">元气恢复进度</div>
                        <div class="progress-score">
                          <span class="score-numerator">{{ calculateEnergyScore(slot.item) }}</span>
                          <span class="score-separator"> / </span>
                          <span class="score-denominator">{{ slot.item.totalSeconds || 0 }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="middle-info">
                <div class="brain-waves">
                  <div class="wave-grid">
                    <div class="wave-item">
                      <div class="wave-label">Delta:</div>
                      <div class="wave-value">{{ getBrainwaveDisplay(slot.item, 'delta') }} %</div>
                    </div>
                    <div class="wave-item">
                      <div class="wave-label">Beta:</div>
                      <div class="wave-value">{{ getBrainwaveDisplay(slot.item, 'beta') }} %</div>
                    </div>
                    <div class="wave-item">
                      <div class="wave-label">Alpha:</div>
                      <div class="wave-value">{{ getBrainwaveDisplay(slot.item, 'alpha') }} %</div>
                    </div>
                    <div class="wave-item">
                      <div class="wave-label">Theta:</div>
                      <div class="wave-value">{{ getBrainwaveDisplay(slot.item, 'theta') }} %</div>
                    </div>
                    <div class="wave-item">
                      <div class="wave-label">Gamma:</div>
                      <div class="wave-value">{{ getBrainwaveDisplay(slot.item, 'gamma') }} %</div>
                    </div>
                  </div>
                </div>
                <div class="wave-chart-wrapper">
                  <div class="wave-chart" :class="getTreatmentStatusClass(slot.item)">
                    <div
                      :id="getChartDomId(slot.slotIndex)"
                      :data-slot-key="getChartSlotKey(slot.slotIndex)"
                      class="echarts-container"
                      ref="waveCharts"
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import config from '@/config';
import sleepViewChartsMixin from '@/composables/sleepViewChartsMixin';
import screenLifecycleMixin from '@/utils/screenLifecycleMixin';

export default {
  mixins: [sleepViewChartsMixin, screenLifecycleMixin],
  name: 'SleepViewWelcome',
  props: {
    displayedAppointments: { type: Array, default: () => [] },
    patientSlotPool: { type: Array, default: () => [] },
    currentTime: { type: String, default: '' },
    currentWeekday: { type: String, default: '' },
    currentDate: { type: String, default: '' },
    ongoingTreatmentCount: { type: Number, default: 0 },
    isSliding: { type: Boolean, default: false },
    welcomeMessage: { type: String, default: '' },
    welcomeAnimationState: { type: String, default: '' },
    welcomeMessageStyle: { type: Object, default: () => ({}) },
    helpers: { type: Object, required: true },
    currentPage: { type: Number, default: 0 },
    pageEpoch: { type: Number, default: 0 }
  },
  data() {
    return {
      chartViewPrefix: 'welcome',
      lastChartUpdateTime: 0,
      preloadedChartOptions: {},
      preloadedPatientKeys: {},
      bubbleTimers: [],
      chartResizeTimers: []
    };
  },
  watch: {
    /**
     * 第二阶段：Welcome/Default 由 keep-alive 缓存；图表实例在 sleep.vue chartPool 中跨切换复用。
     */
  },
  beforeDestroy() {
    if (this.bubbleTimers && this.bubbleTimers.length > 0) {
      this.bubbleTimers.forEach(id => clearTimeout(id));
      this.bubbleTimers = [];
    }
    if (this.chartResizeTimers && this.chartResizeTimers.length > 0) {
      this.chartResizeTimers.forEach(tid => clearTimeout(tid));
      this.chartResizeTimers = [];
    }
    // 第二阶段：不在此 dispose chartPool，避免 Welcome↔Default 切换时反复销毁
    this.preloadedChartOptions = {};
    this.preloadedPatientKeys = {};
  },
  created() {
    this.preloadedChartOptions = {};
    this.preloadedPatientKeys = {};
  },
  methods: {
    getPerformanceConfig() {
      return (this.helpers && this.helpers.performanceConfig) || config.performance || {};
    },
    getTreatmentStatus(item) { return this.helpers.getTreatmentStatus(item) },
    getTreatmentStatusClass(item) { return this.helpers.getTreatmentStatusClass(item) },
    formatTimeDisplay(s) { return this.helpers.formatTimeDisplay(s) },
    formatTherapistName(n) { return this.helpers.formatTherapistName(n) },
    getCharacterImage(item) { return this.helpers.getCharacterImage(item) },
    calculateEnergyScore(item) { return this.helpers.calculateEnergyScore(item) },
    getBrainwaveDisplay(item, type) { return this.helpers.getBrainwaveDisplay(item, type) },
    getTreatmentStatusIcon(item) { return this.helpers.getTreatmentStatusIcon(item) },
    getSleepStatus(item) { return this.helpers.getSleepStatus(item) },
    getProgressPercentage(item) { return this.helpers.getProgressPercentage(item) },
    formatPatientName(n) { return this.helpers.formatPatientName(n) },
  }
}
</script>
